import { ValueOf } from '@/common/common.types';
import { formatSubmittedTimeOrBlank } from '@/dashboard/components/ContributionMeta/contributionMeta.utils';
import {
  GetUnacceptedBatchesResponse,
  SummarizeMinimizedResponse,
} from '@/dashboard/services/dashboardApi';
import {
  dashboardBandSectionBuilder,
  DashboardBandSectionsType,
  DashboardRowPeriodDetails,
  getErrorsFromSummarize,
  getLabelStatusOfBatch,
  hideAllButtons,
  initializePeriodCalculations,
  isSubmitActive,
} from '@/dashboard/utils/DashboardCalculations/dashboardCalculations.utils';

class PeriodCalculator {
  periodCalculations: ValueOf<DashboardRowPeriodDetails>;
  counts: ValueOf<SummarizeMinimizedResponse>;
  unacceptedBatches: GetUnacceptedBatchesResponse;
  period: string;

  constructor(
    period: string,
    counts: ValueOf<SummarizeMinimizedResponse>,
    unacceptedBatches: GetUnacceptedBatchesResponse,
  ) {
    this.period = period;
    this.counts = counts;
    this.unacceptedBatches = unacceptedBatches;
    this.periodCalculations = initializePeriodCalculations(period);
  }

  getPeriodLabel() {
    const label = getLabelStatusOfBatch(this.counts);
    if (label === undefined) {
      throw new Error(`Could not find status of ${this.period}`);
    }
    this.periodCalculations.label = label;
  }

  getButtonStatus() {
    const label = this.periodCalculations.label;
    if (label === 'Accepted') {
      this.periodCalculations.buttons = hideAllButtons();
    } else if (label === 'No records found') {
      this.periodCalculations.buttons = {
        upload: true,
        submit: null,
        validate: null,
      };
    } else {
      const isActive = isSubmitActive(this.counts, this.unacceptedBatches, this.period);
      this.periodCalculations.buttons = {
        upload: true,
        validate: true,
        submit: isActive,
      };
      if (isActive) {
        const isBypasssApproved = this.counts.bypassTimestamp !== null;
        const submittedDate = this.counts.lastSubmission;
        if (!isBypasssApproved) {
          this.periodCalculations.label = formatSubmittedTimeOrBlank(submittedDate, 'Validated');
        }
      }
    }
  }

  calculateErrors() {
    const errors = getErrorsFromSummarize(this.counts);
    this.periodCalculations.bands = {
      ...this.periodCalculations.bands,
      ...errors,
    };
  }

  calculateUnsubmitted() {
    const label = this.periodCalculations.label;
    const isAccepted = label === 'Accepted';
    this.periodCalculations.bands = {
      ...this.periodCalculations.bands,
      unsubmitted: isAccepted ? 0 : this.counts.totals.contributionRecords,
      submitted: isAccepted ? this.counts.totals.contributionRecords : 0,
    };
  }

  buildBands() {
    const isBypassValidated = this.counts.bypassTimestamp !== null;
    const totalEmployees = this.counts.totals.employeeRecords;

    const bands = [
      dashboardBandSectionBuilder.buildUnsubmittedSection(
        this.periodCalculations.bands.unsubmitted,
        totalEmployees,
        this.periodCalculations.bands.submitted,
        this.periodCalculations.buttons.submit === true,
      ),
      dashboardBandSectionBuilder.buildAcceptedSection(
        this.periodCalculations.bands.submitted,
        this.periodCalculations.bands.postSubmitErrors,
      ),
      dashboardBandSectionBuilder.buildPostsubmitErrorSection(
        this.periodCalculations.bands.postSubmitErrors,
        isBypassValidated,
      ),
      dashboardBandSectionBuilder.buildCriticalErrorSection(
        this.periodCalculations.bands.criticalErrors,
        isBypassValidated,
      ),
      dashboardBandSectionBuilder.buildPresubmitErrorSection(
        this.periodCalculations.bands.errors,
        isBypassValidated,
      ),
      dashboardBandSectionBuilder.buildWarningsSection(
        this.periodCalculations.bands.warnings,
        isBypassValidated,
      ),
    ].filter((section) => section !== undefined);

    if (bands.length > 4) {
      const excess = bands.length - 4;
      this.periodCalculations.bandSections = bands.slice(excess) as DashboardBandSectionsType;
    } else {
      this.periodCalculations.bandSections = bands as DashboardBandSectionsType;
    }
  }

  getPeriodCalculations() {
    return this.periodCalculations;
  }
}

export default PeriodCalculator;
